import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { IntlProvider as IntlProviderOriginal, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { en } from '../../intl/en'
import { pl } from '../../intl/pl'
import { LanguageService, SupportedLocale } from '../../services/language/LanguageService'
import { IStoreState } from '../../services/store/IStoreState'
import { IntlService } from '../../services/intl/IntlService'

const IntlServiceBridge: React.FC = () => {
  const intl = useIntl()
  useEffect(() => {
    IntlService.setIntl(intl)
  }, [intl])
  return null
}

const messages = {
  en,
  pl,
}

interface LanguageContextType {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => Promise<void>
  availableLanguages: Array<{ code: SupportedLocale, name: string, nativeName: string }>
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export interface IOwnProps {
}

export type IProps = IOwnProps

const IntlProvider: React.FC<IProps> = (props) => {
  const { children } = props
  const [locale, setLocaleState] = useState<SupportedLocale>('en')
  const [isLoading, setIsLoading] = useState(true)
  const lastAppliedServerLocale = useRef<string | null>(null)

  const serverDefaultLanguage = useSelector(
    (state: IStoreState) => state.Dashboard.defaultLanguage
  )

  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        const storedLanguage = await LanguageService.getStoredLanguage()
        setLocaleState(storedLanguage)
      } catch (error) {
        console.error('Failed to load language preference:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeLanguage()
  }, [])

  const setLocale = async (newLocale: SupportedLocale) => {
    try {
      await LanguageService.setLanguage(newLocale)
      setLocaleState(newLocale)
    } catch (error) {
      console.error('Failed to change language:', error)
      throw error
    }
  }

  useEffect(() => {
    if (serverDefaultLanguage === null || serverDefaultLanguage === undefined) return
    // Skip when the server re-emits the same value we already applied this session —
    // preserves any manual LanguageSwitcher choice. A different value (e.g. after
    // logout/login with another account) still applies.
    if (serverDefaultLanguage === lastAppliedServerLocale.current) return

    lastAppliedServerLocale.current = serverDefaultLanguage

    if (LanguageService.isSupported(serverDefaultLanguage)) {
      setLocale(serverDefaultLanguage).catch(() => {})
    } else {
      console.warn(
        `Unsupported default_language "${serverDefaultLanguage}" received from server; falling back to "en".`
      )
      setLocale('en').catch(() => {})
    }
  }, [serverDefaultLanguage])

  const contextValue: LanguageContextType = {
    locale,
    setLocale,
    availableLanguages: LanguageService.getAvailableLanguages(),
  }

  if (isLoading) {
    return null // Or a loading spinner if preferred
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      <IntlProviderOriginal
        locale={locale}
        defaultLocale='en'
        messages={messages[locale]}
      >
        <IntlServiceBridge />
        {children}
      </IntlProviderOriginal>
    </LanguageContext.Provider>
  )
}

export default IntlProvider
